/**
 * Auth Client Service
 * Based on auth-system.md specification
 *
 * Handles all client-side authentication operations including:
 * - OAuth (Microsoft / Azure AD)
 * - Session management
 * - User profile reads
 *
 * Password, sign-up and magic-link methods were removed when Microsoft sign-in
 * became the only way into the application.
 */

import { ClientService, type ServiceResult } from "../base-client.service";
import type {
  User,
  Session,
  UserProfile,
  OAuthProvider,
  OAuthOptions,
} from "@/lib/supabase/types";
import type { AuthChangeEvent } from "@supabase/supabase-js";

export class AuthClientService extends ClientService {
  constructor() {
    super("AuthClientService");
  }

  // ============================================
  // OAuth Authentication
  // ============================================

  /**
   * Sign in with OAuth provider (Google, GitHub, etc.)
   */
  async signInWithOAuth(
    provider: OAuthProvider,
    options?: Omit<OAuthOptions, "provider">,
  ): Promise<ServiceResult<{ url: string }>> {
    return this.execute(async (client) => {
      this.log("Initiating OAuth sign in", { provider });

      const { data, error } = await client.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo:
            options?.redirectTo || `${window.location.origin}/auth/callback`,
          scopes: options?.scopes,
          queryParams: options?.queryParams,
        },
      });

      if (error) {
        this.logError("OAuth sign in failed", error);
        throw error;
      }

      if (!data.url) {
        throw new Error("OAuth sign in failed: No redirect URL returned");
      }

      this.log("OAuth redirect URL generated");
      return { url: data.url };
    });
  }

  // ============================================
  // Session Management
  // ============================================

  /**
   * Sign out the current user
   */
  async signOut(): Promise<ServiceResult<void>> {
    return this.execute(async (client) => {
      this.log("Signing out");

      const { error } = await client.auth.signOut();

      if (error) {
        this.logError("Sign out failed", error);
        throw error;
      }

      this.log("Sign out successful");
    });
  }

  /**
   * Get current session (from local storage, no server validation)
   */
  async getSession(): Promise<ServiceResult<Session | null>> {
    return this.execute(async (client) => {
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      return data.session;
    });
  }

  /**
   * Get current user (validates JWT with server)
   */
  async getUser(): Promise<ServiceResult<User | null>> {
    return this.execute(async (client) => {
      const { data, error } = await client.auth.getUser();
      if (error) throw error;
      return data.user;
    });
  }

  /**
   * Refresh the current session
   */
  async refreshSession(): Promise<ServiceResult<Session | null>> {
    return this.execute(async (client) => {
      this.log("Refreshing session");

      const { data, error } = await client.auth.refreshSession();
      if (error) throw error;

      this.log("Session refreshed");
      return data.session;
    });
  }

  // ============================================
  // User Profile Management
  // ============================================

  /**
   * Get user profile from es_users table
   */
  async getUserProfile(
    userId: string,
  ): Promise<ServiceResult<UserProfile | null>> {
    return this.execute(async (client) => {
      this.log("Fetching user profile", { userId });

      // Fetch without role join - usePermissions will fetch role separately
      // This avoids RLS issues with the es_roles join
      const { data, error } = await client
        .from("es_users")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return null;
      }

      return data as UserProfile;
    });
  }

  /**
   * Update user profile in es_users table
   */
  async updateUserProfile(
    userId: string,
    updates: Partial<Omit<UserProfile, "id" | "created_at" | "updated_at">>,
  ): Promise<ServiceResult<UserProfile>> {
    return this.execute(async (client) => {
      this.log("Updating user profile", { userId });

      const { data, error } = await client
        .from("es_users")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId)
        .select()
        .single();

      if (error) throw error;

      this.log("Profile updated successfully");
      return data as UserProfile;
    });
  }

  // ============================================
  // Auth State Subscription
  // ============================================

  /**
   * Subscribe to auth state changes
   * Returns an unsubscribe function
   */
  async onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ): Promise<() => void> {
    const client = await this.getClient();

    if (!client) {
      this.log("Supabase not configured, skipping auth state subscription");
      return () => {};
    }

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange(callback);

    return () => {
      subscription.unsubscribe();
    };
  }

  /**
   * Exchange authorization code for session (OAuth callback)
   */
  async exchangeCodeForSession(
    code: string,
  ): Promise<ServiceResult<Session | null>> {
    return this.execute(async (client) => {
      this.log("Exchanging code for session");

      const { data, error } = await client.auth.exchangeCodeForSession(code);

      if (error) {
        this.logError("Code exchange failed", error);
        throw error;
      }

      this.log("Session obtained from code");
      return data.session;
    });
  }
}
